import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useFlowStore } from '../../store/FlowStateStore.ts';
import { Environment as environment } from '../../environment.ts';
import { ChevronDown, Variable, Search, AlertCircle, Loader2 } from 'lucide-react';

interface Attribute {
    attributeId: string;
    attributeName: string;
    attributeType: 'TEXT' | 'NUMBER' | 'ARRAY' | 'OBJECT' | 'BOOLEAN';
    isSystem?: boolean;
}

interface VariablePickerProps {
    onSelect: (attributeName: string) => void;
    label?: string;
    filterType?: 'TEXT' | 'NUMBER' | 'ARRAY' | 'OBJECT' | 'BOOLEAN' | 'ALL';
}

const DROPDOWN_WIDTH = 240;
const DROPDOWN_HEIGHT = 300;
const VIEWPORT_MARGIN = 8;

const VariablePicker: React.FC<VariablePickerProps> = ({ onSelect, label = "Add Variable", filterType = 'ALL' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [attributes, setAttributes] = useState<Attribute[]>([]);
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [coords, setCoords] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number }>({ left: 0, top: 0, maxHeight: 300 });
    const { businessId } = useFlowStore();
    const rootRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const fetchAttributes = async () => {
        if (!businessId) {
            setError("Business ID missing");
            return;
        }

        setIsLoading(true);
        setError(null);

        const payload = {
            businessId: businessId,
            page: 0,
            size: 500,
            templateStatus: null,
            searchText: ''
        };

        try {
            const [attrRes, sysRes] = await Promise.all([
                axios.put(`${environment.baseUrl}/fetch/attribute`, payload, { timeout: 15000 }),
                axios.get(`${environment.baseUrl}/meta/system-attributes`, { timeout: 10000 })
            ]);

            // Extract business attributes
            let bizData: Attribute[] = [];
            const resData = attrRes.data;
            if (resData) {
                const raw = resData.data?.data || resData.data || resData;
                if (Array.isArray(raw)) {
                    bizData = raw.map((a: any) => ({
                        attributeId: a.attributeId || a.id || Math.random().toString(),
                        attributeName: a.attributeName || a.name || "",
                        attributeType: (a.attributeType || "TEXT") as any,
                        isSystem: false
                    }));
                }
            }

            // Extract system attributes (check for .data wrapper)
            let sysData: Attribute[] = [];
            const rawSys = sysRes.data?.data || sysRes.data;
            if (Array.isArray(rawSys)) {
                sysData = rawSys.map((s: string) => ({
                    attributeId: `sys-${s}`,
                    attributeName: s,
                    attributeType: 'TEXT' as any,
                    isSystem: true
                }));
            }

            // Combine and sort alphabetically
            const combined = [...bizData, ...sysData].sort((a, b) =>
                a.attributeName.localeCompare(b.attributeName)
            );

            setAttributes(combined);
            if (combined.length === 0) setError("No variables found");
        } catch (err) {
            console.error("VariablePicker Error:", err);
            setError("Failed to sync variables");
        } finally {
            setIsLoading(false);
        }
    };

    const toggleOpen = () => {
        if (!isOpen && rootRef.current) {
            const rect = rootRef.current.getBoundingClientRect();
            const left = Math.min(
                Math.max(rect.right - DROPDOWN_WIDTH, VIEWPORT_MARGIN),
                window.innerWidth - DROPDOWN_WIDTH - VIEWPORT_MARGIN
            );
            const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
            const spaceAbove = rect.top - VIEWPORT_MARGIN;
            const openUpward = spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow;

            // Fixed target height; only shrink (down to a usable floor) when the
            // chosen side genuinely doesn't have room for it.
            setCoords(
                openUpward
                    ? { left, bottom: window.innerHeight - rect.top + VIEWPORT_MARGIN, maxHeight: Math.min(DROPDOWN_HEIGHT, Math.max(spaceAbove, 150)) }
                    : { left, top: rect.bottom + VIEWPORT_MARGIN, maxHeight: Math.min(DROPDOWN_HEIGHT, Math.max(spaceBelow, 150)) }
            );
        }
        setIsOpen((prev) => !prev);
    };

    useEffect(() => {
        if (isOpen) {
            fetchAttributes();
            const handleClickOutside = (event: MouseEvent) => {
                const target = event.target as Node;
                const insideRoot = !!rootRef.current && rootRef.current.contains(target);
                const insideDropdown = !!dropdownRef.current && dropdownRef.current.contains(target);
                if (!insideRoot && !insideDropdown) {
                    setIsOpen(false);
                }
            };
            // Capture phase: React Flow's own drag/pan handling can stopPropagation() on
            // mousedown during the bubble phase, which would otherwise stop this listener
            // (attached on document) from ever firing when clicking elsewhere on the canvas.
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => document.removeEventListener('mousedown', handleClickOutside, true);
        }
    }, [isOpen, businessId]);

    const filteredAttributes = (filterType === 'ALL'
        ? attributes
        : attributes.filter(a => a.attributeType?.toUpperCase() === filterType.toUpperCase()))
        .filter(a => a.attributeName.toLowerCase().includes(search.toLowerCase()));

    const getTypeColor = (type: string) => {
        switch(type) {
            case 'ARRAY': return '#8b5cf6';
            case 'OBJECT': return '#f59e0b';
            case 'NUMBER': return '#10b981';
            case 'BOOLEAN': return '#ef4444';
            default: return '#0ea5e9';
        }
    };

    return (
        <div className="variable-picker-root nodrag nopan" ref={rootRef}>
            <button
                className={`picker-trigger ${isOpen ? 'active shadow-md border-blue-400' : ''}`}
                onClick={toggleOpen}
                type="button"
            >
                <Variable size={12} className={isOpen ? 'text-blue-500' : 'text-slate-400'} />
                <span>{label}</span>
                <ChevronDown size={12} className={`transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-500' : 'text-slate-400'}`} />
            </button>

            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    className="picker-dropdown premium-card shadow-2xl nodrag nopan"
                    style={{ left: coords.left, top: coords.top, bottom: coords.bottom, maxHeight: coords.maxHeight }}
                >
                    <div className="picker-search">
                        <Search size={12} className="text-slate-400" />
                        <input
                            autoFocus
                            placeholder="Search attributes..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="picker-list">
                        {isLoading ? (
                            <div className="picker-state-msg">
                                <Loader2 size={16} className="animate-spin text-blue-500" />
                                <span>Syncing variables...</span>
                            </div>
                        ) : error && filteredAttributes.length === 0 ? (
                            <div className="picker-state-msg text-amber-600">
                                <AlertCircle size={14} />
                                <span>{error}</span>
                                <button onClick={(e) => { e.stopPropagation(); fetchAttributes(); }} className="text-[9px] underline mt-1">Retry</button>
                            </div>
                        ) : filteredAttributes.length > 0 ? filteredAttributes.map(attr => (
                            <div
                                key={attr.attributeId}
                                className="picker-item"
                                onClick={() => {
                                    onSelect(attr.attributeName);
                                    setIsOpen(false);
                                    setSearch("");
                                }}
                            >
                                <span className="name">{attr.attributeName}</span>
                                <div className="flex items-center gap-2">
                                    {attr.isSystem && (
                                        <span className="text-[8px] font-bold bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded leading-none uppercase">
                                            System
                                        </span>
                                    )}
                                    <span
                                        className="type-badge"
                                        style={{ backgroundColor: getTypeColor(attr.attributeType) + '15', color: getTypeColor(attr.attributeType) }}
                                    >
                                        {attr.attributeType}
                                    </span>
                                </div>
                            </div>
                        )) : (
                            <div className="picker-state-msg text-slate-400 italic">
                                No attribute matches
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}

            <style>{`
                .variable-picker-root {
                    position: relative;
                    display: inline-block;
                }
                .picker-trigger {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 11px;
                    font-weight: 600;
                    color: #475569;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .picker-trigger:hover {
                    border-color: var(--primary-bg-color);
                    color: var(--primary-bg-color);
                    background: #f0f9ff;
                }
                .picker-trigger.active {
                    border-color: var(--primary-bg-color);
                    background: #f0f9ff;
                    color: var(--primary-bg-color);
                }
                .picker-dropdown {
                    position: fixed;
                    width: 240px;
                    display: flex;
                    flex-direction: column;
                    background: white;
                    z-index: 10002;
                    overflow: hidden;
                    animation: pickerSlide 0.2s ease-out;
                    border: 1px solid #e2e8f0;
                }
                @keyframes pickerSlide {
                    from { opacity: 0; transform: translateY(-8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .picker-search {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px;
                    border-bottom: 1px solid #f1f5f9;
                    background: #f8fafc;
                    flex-shrink: 0;
                }
                .picker-search input {
                    border: none;
                    background: none;
                    outline: none;
                    font-size: 12px;
                    color: #1e293b;
                    width: 100%;
                }
                .picker-list {
                    flex: 1;
                    min-height: 0;
                    overflow-y: auto;
                    padding: 6px;
                }
                .picker-item {
                    padding: 10px 12px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .picker-item:hover {
                    background: #f1f5f9;
                    transform: translateX(2px);
                }
                .picker-item .name {
                    font-size: 12px;
                    font-weight: 600;
                    color: #334155;
                }
                .picker-item:hover .name {
                    color: var(--primary-bg-color);
                }
                .type-badge {
                    font-size: 8px;
                    font-weight: 800;
                    padding: 2px 6px;
                    border-radius: 4px;
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                }
                .picker-state-msg {
                    padding: 32px 16px;
                    font-size: 12px;
                    color: #64748b;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                }
            `}</style>
        </div>
    );
};

export default VariablePicker;
